import { Table, Tag } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo } from 'react'
import type {
  OverlapEnrollment,
  OverlapPair,
} from '../api/enrollment-overlap.types'
import { formatDate } from '../utils/run-display'
import { describeEnrollment } from '../utils/timeline'

interface OverlapPairsTableProps {
  enrollments: OverlapEnrollment[]
  overlaps: OverlapPair[]
}

/**
 * One row per overlapping pair. With 3+ enrollments not every two overlap, so
 * this is a list of pairs, not of enrollments. Enrollments are referenced by id.
 */
export default function OverlapPairsTable({
  enrollments,
  overlaps,
}: OverlapPairsTableProps) {
  const labelById = useMemo(
    () =>
      new Map(
        enrollments.map((enrollment) => [
          enrollment.id,
          describeEnrollment(enrollment),
        ])
      ),
    [enrollments]
  )
  const label = (id: string) => labelById.get(id) ?? id

  const columns: ColumnsType<OverlapPair> = [
    {
      title: 'Enrollment A',
      key: 'enrollment1',
      render: (_, pair) => label(pair.enrollment1Id),
    },
    {
      title: 'Enrollment B',
      key: 'enrollment2',
      render: (_, pair) => label(pair.enrollment2Id),
    },
    {
      title: 'Overlap window',
      key: 'window',
      render: (_, pair) => (
        <Tag color="error">
          {formatDate(pair.overlapStart)} – {formatDate(pair.overlapEnd)}
        </Tag>
      ),
    },
  ]

  return (
    <Table<OverlapPair>
      dataSource={overlaps}
      columns={columns}
      rowKey={(pair) => `${pair.enrollment1Id}|${pair.enrollment2Id}`}
      pagination={false}
      locale={{ emptyText: 'No overlapping pairs' }}
    />
  )
}
